import { useCallback, RefObject } from "react";
import { AgGridReact } from "ag-grid-react";
import useUiuxStore from "src/store/useUiuxStore";
import { focusToCell } from "src/utils/grid/GridHandlerContent";
import { PRD2010_R01Dto } from "../Dto_PRD2010_R01";

interface UsePRD2010_R01ModalProps {
	PRD2010_R01List: PRD2010_R01Dto[];
	updatePRD2010_R01List: (index: number, data: PRD2010_R01Dto) => void;
	originalPRD2010_R01List: PRD2010_R01Dto[];
	gridRef: RefObject<AgGridReact<PRD2010_R01Dto> | null>;
}

export function usePRD2010_R01Modal({
	PRD2010_R01List,
	updatePRD2010_R01List,
	originalPRD2010_R01List,
	gridRef,
}: UsePRD2010_R01ModalProps) {
	const { setCodeSearchModalProps } = useUiuxStore();

	const handlePopupClose = useCallback(() => {
		if (gridRef.current?.api) {
			gridRef.current.api.stopEditing();
		}
	}, [gridRef]);

	const settingCodeSearchModalProps = useCallback(
		(
			category: string,
			search: string,
			isInfo: boolean,
			handleFocusId: string,
			originalData: PRD2010_R01Dto,
			valueIndex: number,
		) => {
			const handleClear = () => {
				const updateData: Partial<PRD2010_R01Dto> = {
					...originalData,
					isModified: originalData.inserted ? false : true,
				};

				if (handleFocusId === "itemCd") {
					updateData.itemCd = "";
					updateData.itemNm = "";
					updateData.itemSpec = "";
					updateData.itemUnit = "";
				} else if (handleFocusId === "basOrderNo") {
					updateData.basOrderNo = "";
					updateData.basOrderItemNo = "";
					updateData.customNm = "";
					updateData.orderQty = 0;
				} else if (handleFocusId === "orderEmp") {
					updateData.orderEmp = "";
					updateData.orderEmpNm = "";
				}

				updatePRD2010_R01List(valueIndex, updateData as PRD2010_R01Dto);
				handlePopupClose();
			};

			const wrapperOptionMap: Record<string, { size: string; width: number; overlay: boolean }> = {
				itemCd: { size: "md", width: 50, overlay: true },
				basOrderNo: { size: "md", width: 1200, overlay: true },
				orderEmp: { size: "sm", width: 20, overlay: true },
			};

			setCodeSearchModalProps({
				category,
				search,
				formName: "PRD2010_R01",
				isInfo,
				handleFoucsId: handleFocusId,
				//extraFilter: handleFocusId === 'basOrderNo'
                // ? { itemCd: originalData.itemCd || '' }
                // : undefined,
				onClear: handleClear,
				onConfirm: (data: any) => {
					const currentRow =
						PRD2010_R01List.find((item) => item.valueIndex === valueIndex) ??
						originalPRD2010_R01List.find((item) => item.valueIndex === valueIndex) ??
						originalData;

					const updateData: Partial<PRD2010_R01Dto> = {
						...currentRow,
						isModified: originalData.inserted ? false : true,
					};

					if (handleFocusId === "itemCd") {
						updateData.itemCd = data.itemCd ?? "";
						updateData.itemNm = data.itemNm ?? "";
						updateData.itemSpec = data.itemSpec ?? "";
						updateData.itemUnit = data.itemUnit ?? "";
					} else if (handleFocusId === "basOrderNo") {
						// //updateData.basOrderNo = data.basOrderNo ?? "";
                        // updateData.basOrderItemNo = data.basOrderItemNo ?? "";

						updateData.basOrderNo = data.salesOrderNo ?? data.basOrderNo ?? "";
						updateData.basOrderItemNo = data.itemNo ?? data.basOrderItemNo ?? "";
						updateData.customNm = data.customNm ?? "";

						// updateData.itemCd = data.itemCd ?? "";
                        // updateData.itemNm = data.itemNm ?? "";
                        // updateData.itemSpec = data.itemSpec ?? "";
                        // updateData.itemUnit = data.orderUnit ?? data.itemUnit ?? "";
                        // updateData.orderQty = Number(data.orderQty) || 0;
						
						// 기존 값이 비어있을 때만 새로운 값으로 덮어쓰기
						if (!updateData.itemCd) updateData.itemCd = data.itemCd ?? "";
						if (!updateData.itemNm) updateData.itemNm = data.itemNm ?? "";
						if (!updateData.itemSpec) updateData.itemSpec = data.itemSpec ?? "";
						if (!updateData.itemUnit) updateData.itemUnit = data.orderUnit ?? data.itemUnit ?? "";
						if (!updateData.orderQty) updateData.orderQty = Number(data.orderQty) || 0;
					} else if (handleFocusId === "orderEmp") {
						updateData.orderEmp = data.empNo ?? "";
						updateData.orderEmpNm = data.empNm ?? "";
					}

					updatePRD2010_R01List(valueIndex, updateData as PRD2010_R01Dto);

					setTimeout(() => {
						if (!gridRef.current) return;
						if (handleFocusId === "itemCd") {
							focusToCell(gridRef, valueIndex, "orderQty");
						} else if (handleFocusId === "orderEmp") {
							focusToCell(gridRef, valueIndex, "itemCd");
						} else if (handleFocusId === "basOrderNo") {
							focusToCell(gridRef, valueIndex, "customNm");
						}
					}, 100);
				},
				wrapperOption: wrapperOptionMap[handleFocusId] ?? { size: "md", width: 50, overlay: true },
			});
		},
		[
			setCodeSearchModalProps,
			updatePRD2010_R01List,
			PRD2010_R01List,
			originalPRD2010_R01List,
			gridRef,
			handlePopupClose,
		],
	);

	return { settingCodeSearchModalProps, handlePopupClose };
}
